import { useState, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Mic, Square, Loader2 } from 'lucide-react';

export default function VoiceRecorder({ onTranscription, className = '', size = 'default' }) {
  const { axiosAuth } = useAuth();
  const [recording, setRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        setProcessing(true);
        try {
          const api = axiosAuth();
          const formData = new FormData();
          formData.append('audio', blob, 'recording.webm');
          const res = await api.post('/voice/transcribe', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          if (onTranscription) onTranscription(res.data.text);
        } catch (err) {
        } finally {
          setProcessing(false);
        }
      };

      mediaRecorder.start();
      setRecording(true);
    } catch (err) {
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      setRecording(false);
    }
  };

  if (size === 'icon') {
    return processing ? (
      <Button disabled size="icon" variant="ghost" className="shrink-0 h-8 w-8"><Loader2 size={14} className="animate-spin" /></Button>
    ) : recording ? (
      <Button onClick={stopRecording} size="icon" variant="ghost" className="shrink-0 h-8 w-8 text-red-500 animate-pulse" data-testid="stop-recording-button"><Square size={14} /></Button>
    ) : (
      <Button onClick={startRecording} size="icon" variant="ghost" className="shrink-0 h-8 w-8 text-teal-600 hover:text-teal-700 hover:bg-teal-50" data-testid="start-recording-button"><Mic size={14} /></Button>
    );
  }

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {processing ? (
        <Button disabled className="rounded-full gap-2" variant="outline" size="sm">
          <Loader2 size={14} className="animate-spin" /> Processing...
        </Button>
      ) : recording ? (
        <Button onClick={stopRecording} variant="destructive" className="rounded-full gap-2 animate-pulse" size="sm" data-testid="stop-recording-button">
          <Square size={14} /> Stop
        </Button>
      ) : (
        <Button onClick={startRecording} variant="outline" className="rounded-full gap-2 border-teal-300 text-teal-700 hover:bg-teal-50" size="sm" data-testid="start-recording-button">
          <Mic size={14} /> Speak
        </Button>
      )}
      {recording && <span className="text-xs text-red-500 animate-pulse">Recording...</span>}
    </div>
  );
}
